import { Button, useTheme } from '@ytl/common-web';
import { useEffect, useMemo, useState } from 'react';
//import { useNavigate } from 'react-router-dom';
import { ReactComponent as WarningIcon } from '../../assets/warning.svg';
import { useLanguage } from '../../contexts/languageContext';
import { YeppFlowPhase } from '../../pages/YeppFlowPage';
import { WidgetEvent } from '../../shared/@types/widgetEvents';
import { Banned } from '../../shared/components/partials/verification/Banned';
import { getConfig } from '../../shared/data/config';
import { emitWidgetEvent, onProcessPhase, onWidgetEvent } from '../../shared/utils/emitData';
import { createUnsignedJWT, parseJWT } from '../../shared/utils/jwtUtils';
import { VerificationComponent } from '../verificationComponent/VerificationComponent';

export const YeppFlowWebView = () => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  //const navigate = useNavigate();

  // Get URL params
  const urlParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      salesMsisdn: params.get('salesmsisdn') || '',
      backUrl: params.get('backurl') || '',
    };
  }, []);

  const initParamsJWT = useMemo(() => {
    const payload = {
      clientID: 'alm-client-001',
      sessionID: 'YeppRegistration-' + Date.now(),
      withPassword: true,
      captchaNeeded: true,
      EmailVerification: true,
      TMSVerification: true,
      MSISDNVerification: true,
      createDUP: true,
      salesMsisdn: urlParams.salesMsisdn,
      salesCA: '',
      IPBanningEnabled: true,
      language: '',
      theme: '',
      additionalInfo: {
        application: 'APP',
        channel_type: 'mobil_app',
        process: 'Yepp értékesítés',
        sales_type_cd: '6001',
      },
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
    };
    return createUnsignedJWT(payload);
  }, [urlParams.salesMsisdn]);

  // Decode JWT to get EmailVerification flag and set initial view
  const initialView = useMemo(() => {
    try {
      const payload = parseJWT(initParamsJWT);
      return payload?.EmailVerification
        ? YeppFlowPhase.RegisterForm
        : payload?.salesMsisdn || payload?.salesCA
          ? YeppFlowPhase.PhoneNumberVerificationCA
          : YeppFlowPhase.PhoneNumberVerificationOther;
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return YeppFlowPhase.RegisterForm;
    }
  }, [initParamsJWT]);

  const [view, setView] = useState<YeppFlowPhase>(initialView);

  useEffect(() => {
    const cleanup = onProcessPhase((phase: string) => {
      console.log('Received phase change:', phase);

      switch (phase) {
        case 'GetEmail':
          setView(YeppFlowPhase.RegisterForm);
          break;
        case 'EmailConfirm':
          setView(YeppFlowPhase.EmailVerification);
          break;
        case 'SelectPhoneNumber':
          setView(YeppFlowPhase.PhoneNumberVerificationCA);
          break;
        case 'AddDifferentPhoneNumber':
          setView(YeppFlowPhase.PhoneNumberVerificationOther);
          break;
        case 'PhoneNumberConfirm':
          setView(YeppFlowPhase.PhoneNumberVerificationCode);
          break;
        case 'Finish':
          // Don't change view - keep showing the previous step's header/text
          break;
        case 'Banned':
          setView(YeppFlowPhase.Banned);
          break;
        case 'Error':
          setView(YeppFlowPhase.Error);
          break;
      }
    });

    return cleanup;
  }, []);

  useEffect(() => {
    const cleanup = onWidgetEvent((data, token) => {
      console.log('Received widget event in YeppFlowWebView:', data);

      switch (data.eventName) {
        case WidgetEvent.ProcessFinished:
          if (urlParams.backUrl) {
            const backUrl = new URL(urlParams.backUrl);
            backUrl.searchParams.set('eventName', WidgetEvent.ProcessFinished);
            backUrl.searchParams.set('eventValue', token);
            window.location.href = backUrl.toString();
          }
          break;
        case WidgetEvent.LoginClicked:
        case WidgetEvent.EmailPrecheckLogin:
          if (data.isSuccess && urlParams.backUrl) {
            const backUrl = new URL(urlParams.backUrl);
            backUrl.searchParams.set('eventName', WidgetEvent.LoginClicked);
            backUrl.searchParams.set('eventValue', token);
            window.location.href = backUrl.toString();
          }
          break;
        case WidgetEvent.TechnicalError:
          console.error('Technical error:', data.eventValue);
          break;
      }
    });

    return cleanup;
  }, [urlParams.backUrl]);

  //TODO: ha nem kell useMemo lehet egyszerűsíteni
  const header = useMemo(() => {
    if (view === YeppFlowPhase.RegisterForm) {
      return translation.yeppRegistration.flow.header.registration;
    }
    if (view === YeppFlowPhase.RegisterWrongEmail) {
      return translation.yeppRegistration.flow.header.registration;
    }
    if (view === YeppFlowPhase.RegisterAlreadyRegistered) {
      return translation.yeppRegistration.flow.header.registration;
    }
    if (view === YeppFlowPhase.EmailVerification) {
      return translation.yeppRegistration.flow.header.emailVerification;
    }
    if (view === YeppFlowPhase.PhoneNumberVerificationCA) {
      return translation.yeppRegistration.flow.header.phoneVerification;
    }
    if (view === YeppFlowPhase.PhoneNumberVerificationOther) {
      return translation.yeppRegistration.flow.header.phoneVerification;
    }
    if (view === YeppFlowPhase.PhoneNumberVerificationCode) {
      return translation.yeppRegistration.flow.header.phoneVerification;
    }
    if (view === YeppFlowPhase.Finish) {
      return '';
    }
    if (view === YeppFlowPhase.Banned) {
      return '';
    }
    if (view === YeppFlowPhase.Error) {
      return '';
    }
    return '';
  }, [view, translation]);

  //TODO: ha nem kell useMemo lehet egyszerűsíteni
  const text = useMemo(() => {
    if (view === YeppFlowPhase.RegisterForm) {
      return translation.yeppRegistration.flow.text.enterEmail;
    }
    if (view === YeppFlowPhase.RegisterWrongEmail) {
      return translation.yeppRegistration.flow.text.enterEmail;
    }
    if (view === YeppFlowPhase.RegisterAlreadyRegistered) {
      return translation.yeppRegistration.flow.text.enterEmail;
    }
    if (view === YeppFlowPhase.EmailVerification) {
      return translation.yeppRegistration.flow.text.emailVerification;
    }
    if (view === YeppFlowPhase.PhoneNumberVerificationCA) {
      return translation.yeppRegistration.flow.text.phoneVerification;
    }
    if (view === YeppFlowPhase.PhoneNumberVerificationOther) {
      return translation.yeppRegistration.flow.text.phoneVerification;
    }
    if (view === YeppFlowPhase.PhoneNumberVerificationCode) {
      return translation.yeppRegistration.flow.text.phoneCode;
    }
    if (view === YeppFlowPhase.Finish) {
      return '';
    }
    if (view === YeppFlowPhase.Banned) {
      return '';
    }
    if (view === YeppFlowPhase.Error) {
      return '';
    }
    return '';
  }, [view, translation]);

  const exitButtonAction = () => {
    if (isWebView) {
      window.location.href = getConfig().closeWebViewUrl;
    } else {
      const url = getConfig().webLoginUrl;
      window.location.href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    }
  };

  return (
    <>
      <div style={view === YeppFlowPhase.Banned ? undefined : { display: 'none' }}>
        <Banned buttonAction={exitButtonAction} />
      </div>
      <div
        className="register-page"
        id="yepp-register-page"
        style={view === YeppFlowPhase.Banned ? { display: 'none' } : undefined}
      >
        <div className="register-page__container">
          {
            <div className="register-page__container__box">
              {view !== YeppFlowPhase.Error && (
                <>
                  <h3>{header}</h3>
                  <p dangerouslySetInnerHTML={{ __html: text }}></p>
                  {view === YeppFlowPhase.PhoneNumberVerificationCA && (
                    <div className="onboarding-step__warning">
                      <WarningIcon className="onboarding-step__warning-icon" />
                      <div className="onboarding-step__warning-content">
                        <strong>{translation.yeppRegistration.flow.warning.title}</strong>
                        <p>{translation.yeppRegistration.flow.warning.description}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div style={view === YeppFlowPhase.Error ? { display: 'none' } : undefined}>
                <VerificationComponent initParamsJWT={initParamsJWT} />
              </div>
              {view === YeppFlowPhase.RegisterForm && (
                <>
                  <div className="register-page__container__divider">
                    <span>{translation.loginRegister.register.box.oauthSectionLabel}</span>
                  </div>
                  <div
                    className={`register-page__container__action-container${isWebView ? ' register-page__container__action-container--webview' : ''}`}
                  >
                    <Button
                      variant="contained"
                      onClick={() => {
                        emitWidgetEvent({
                          eventName: WidgetEvent.LoginClicked,
                          eventValue: {},
                          isSuccess: true,
                        });
                      }}
                    >
                      {translation.loginRegister.register.box.loginCommon}
                    </Button>
                  </div>
                </>
              )}
            </div>
          }
        </div>
      </div>
    </>
  );
};
